import React, { useEffect } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';

const content = {
  en: {
    metaTitle: 'Booking Confirmed | Halo Aesthetic Clinic Richmond Hill',
    statusBadge: 'Request Received',
    title: 'Thank You!',
    subtitle: 'Your Consultation Request is Received',
    description: 'Our clinic team will contact you via phone or SMS during business hours to confirm your exact appointment details.',
    serviceLabel: 'Requested Service:',
    contactNotice: 'Need immediate assistance or have urgent questions?',
    callButton: 'Call Us Now: 942-388-1688',
    backHome: 'Back to Home',
    browseServices: 'Browse Other Services',
    businessHoursTitle: 'Clinic Hours',
    businessHoursText: 'Tue - Thu & Sat: 10:00 AM - 6:00 PM | Fri: 1:00 PM - 9:00 PM | Sun & Mon: Closed',
    locationText: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
  },
  cn: {
    metaTitle: '预约确认成功 | Halo Richmond Hill 医美诊所',
    statusBadge: '预约已提交',
    title: '感谢您的预约！',
    subtitle: '您的咨询请求已成功接收',
    description: '我们的诊所团队将在工作时间内通过电话或短信与您联系，确认具体的预约时间与细节。',
    serviceLabel: '预约项目：',
    contactNotice: '如有紧急疑问或需要即时协助，欢迎直接拨打诊所电话：',
    callButton: '直接致电：942-388-1688',
    backHome: '返回首页',
    browseServices: '浏览其他项目',
    businessHoursTitle: '营业时间',
    businessHoursText: '周二至周四及周六：10:00 - 18:00 | 周五：13:00 - 21:00 | 周日、周一：休息',
    locationText: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
  }
};

const BookingSuccessPage = () => {
  const { lang } = useLang();
  const c = content[lang] || content.en;
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Retrieve requested service name from route state or URL query parameter
  const serviceName =
    location.state?.serviceName ||
    searchParams.get('serviceName') ||
    searchParams.get('service');

  useEffect(() => {
    // 1. Google Ads Conversion Event
    const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || 'CONVERSION_LABEL';
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': `AW-18458381832/${conversionLabel}`
      });
    }

    // 2. Meta Pixel Lead Event (if applicable)
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: serviceName || 'General Booking'
      });
    }

    // Scroll smoothly to top of page upon navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [serviceName]);

  return (
    <div className="min-h-[80vh] bg-softGradient bg-no-repeat bg-top text-text py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.haloaestheticclinic.com/booking-success" />
      </Helmet>

      <div className="max-w-2xl w-full bg-white/80 backdrop-blur-md rounded-2xl shadow-card p-8 sm:p-12 border border-champagne/40 text-center relative overflow-hidden">
        {/* Subtle Decorative Background Element */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#2E6B4C]/5 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-champagne/20 rounded-full blur-2xl pointer-events-none" />

        {/* Success Icon */}
        <div className="mx-auto w-20 h-20 sm:w-24 sm:h-24 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner border border-emerald-200/60 animate-bounce-short">
          <svg
            className="w-10 h-10 sm:w-12 sm:h-12 text-[#2E6B4C]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        {/* Status Badge */}
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#2E6B4C]/10 text-[#2E6B4C] mb-4">
          {c.statusBadge}
        </span>

        {/* Headings */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          {c.title}
        </h1>
        <h2 className="text-xl sm:text-2xl font-medium text-[#2E6B4C] mb-4">
          {c.subtitle}
        </h2>

        {/* Description */}
        <p className="text-gray-700 text-base sm:text-lg max-w-lg mx-auto mb-6 leading-relaxed">
          {c.description}
        </p>

        {/* Requested Service Card (if available) */}
        {serviceName && (
          <div className="mb-8 p-4 bg-emerald-50/70 border border-emerald-200/70 rounded-xl text-left sm:text-center">
            <span className="text-sm font-medium text-gray-500 block sm:inline mr-2">
              {c.serviceLabel}
            </span>
            <span className="text-base font-semibold text-[#2E6B4C]">
              {serviceName}
            </span>
          </div>
        )}

        {/* Direct Phone Entrance & Notice */}
        <div className="bg-stone-50 border border-gray-200/80 rounded-xl p-5 mb-8">
          <p className="text-sm text-gray-600 mb-3">{c.contactNotice}</p>
          <a
            href="tel:942-388-1688"
            className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-[#2E6B4C] text-white font-medium shadow-md hover:bg-[#23533a] transition-all hover:scale-[1.02]"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            {c.callButton}
          </a>
        </div>

        {/* Business Hours & Clinic Info */}
        <div className="text-xs text-gray-500 space-y-1 mb-8">
          <p className="font-semibold text-gray-700">{c.businessHoursTitle}</p>
          <p>{c.businessHoursText}</p>
          <p className="pt-1 text-gray-400">{c.locationText}</p>
        </div>

        {/* Navigation Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/"
            className="w-full sm:w-auto px-6 py-3 rounded-xl text-gray-800 border border-gray-300 font-medium hover:bg-gray-100 transition-colors shadow-sm"
          >
            {c.backHome}
          </Link>
          <Link
            to="/services"
            className="w-full sm:w-auto px-6 py-3 rounded-xl text-gray-900 font-medium transition-all shadow-soft hover:shadow-glow border border-champagne/60"
            style={{
              backgroundImage:
                'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
            }}
          >
            {c.browseServices}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
